import { asyncRequest, executeScripts, registerComponent } from "helpers";
import WYATTElement from "wyatt";

/**
 * @param {HTMLElement[]} items
 * @returns {HTMLElement[]}
 */
function shuffleItems(items) {
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

const POLL_ATTEMPTS = 5;
const POLL_INITIAL_DELAY_MS = 500;
const POLL_MAX_DELAY_MS = 8000;

const fetchOptions = {
  cache: "no-store",
  credentials: "same-origin",
};

registerComponent(
  "user-recommendations",
  class UserRecommendations extends WYATTElement {
    constructor() {
      super();

      this.onUpdateDOM = this.handleUpdateDOM.bind(this);

      const observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: "0px 0px 200px 0px",
      });

      observer.observe(this);
    }

    connectedCallback() {
      this.subscribe(WYATT.PubSub.events.updateDOM, this.onUpdateDOM);
    }

    get isLoggedIn() {
      return WYATT.shopify.customer.id != null;
    }

    get erpId() {
      return WYATT.shopify.customer.metafields?.ethel?.erp_id ?? null;
    }

    handleIntersection = async (entries, observer) => {
      if (!entries[0].isIntersecting) {
        return;
      }

      observer.unobserve(this);

      this.renderDisplayedProducts();

      if (!this.isLoggedIn || !this.config.userRecommendationsEndpoint) {
        return;
      }

      await this.syncCustomerRecommendations();
    };

    async syncCustomerRecommendations() {
      const html = await this.fetchUserRecommendations();

      if (!html) {
        return;
      }

      const status = this.parseRecommendationsStatus(html);

      if (this.shouldDisplayCustomerRecommendations(status, html)) {
        this.updateLiveRegion(html);

        return;
      }

      if (!status?.shouldEnqueue || !this.erpId) {
        return;
      }

      const enqueued = await this.enqueueRecommendations();

      if (!enqueued) {
        return;
      }

      await this.pollUserRecommendations();
    }

    handleUpdateDOM(data) {
      const item = data?.find(
        ({ liveRegion }) => liveRegion === this.config.liveRegionKey,
      );

      if (!item) {
        return;
      }

      queueMicrotask(() => {
        this.renderDisplayedProducts();
      });
    }

    renderDisplayedProducts() {
      const liveRegion = this.querySelector(
        `[data-live-region="${this.config.liveRegionKey}"]`,
      );
      const source = liveRegion?.querySelector(
        "template[data-user-recommendations-source]",
      );
      const output = liveRegion?.querySelector(
        "[data-user-recommendations-output]",
      );

      if (!source || !output) {
        return;
      }

      const temp = document.createElement("div");
      temp.appendChild(source.content.cloneNode(true));

      const slider = temp.querySelector("[data-slider]");

      if (!slider) {
        return;
      }

      const slides = Array.from(
        slider.querySelectorAll("[data-user-recommendations-slide]"),
      );
      const limit = this.config.limit ?? 8;
      const selected = shuffleItems(slides).slice(0, limit);

      slider.replaceChildren(...selected);
      output.replaceChildren(...temp.childNodes);
      executeScripts(output);
    }

    parseRecommendationsStatus(html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const statusScript = doc.querySelector(
        "[data-user-recommendations-status]",
      );

      if (!statusScript?.textContent) {
        return null;
      }

      try {
        return JSON.parse(statusScript.textContent);
      } catch {
        return null;
      }
    }

    async enqueueRecommendations() {
      const response = await asyncRequest(
        this.buildRecommendationsUrl(this.erpId),
        {
          ...fetchOptions,
          ...WYATT.config.fetch.post,
        },
        {
          responseFormat: "json",
        },
      );

      return response?.status === "enqueued";
    }

    fetchUserRecommendations() {
      return asyncRequest(
        this.config.userRecommendationsEndpoint,
        fetchOptions,
      );
    }

    async pollUserRecommendations(attempt = 0, delay = POLL_INITIAL_DELAY_MS) {
      if (attempt >= POLL_ATTEMPTS) {
        return;
      }

      await this.sleep(delay);

      const html = await this.fetchUserRecommendations();

      if (
        html &&
        this.shouldDisplayCustomerRecommendations(
          this.parseRecommendationsStatus(html),
          html,
        )
      ) {
        this.updateLiveRegion(html);

        return;
      }

      const nextDelay = Math.min(delay * 2, POLL_MAX_DELAY_MS);

      await this.pollUserRecommendations(attempt + 1, nextDelay);
    }

    buildRecommendationsUrl(erpId) {
      const path = WYATT.config.userRecommendations.endpoints.recommendations;

      return `${path}/${encodeURIComponent(erpId)}`;
    }

    shouldDisplayCustomerRecommendations(status, html) {
      return (
        status?.fresh === true &&
        status?.hasProducts === true &&
        this.hasCustomerRecommendations(html)
      );
    }

    hasCustomerRecommendations(html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const region = doc.querySelector(
        `[data-live-region="${this.config.liveRegionKey}"]`,
      );

      if (!region) {
        return false;
      }

      const source = region.querySelector(
        "template[data-user-recommendations-source]",
      );

      return (
        source?.content.querySelector(
          '[data-user-recommendations-source="customer"]',
        ) != null
      );
    }

    extractLiveRegionHtml(html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const region = doc.querySelector(
        `[data-live-region="${this.config.liveRegionKey}"]`,
      );

      return region?.outerHTML ?? html;
    }

    updateLiveRegion(html) {
      this.publish(WYATT.PubSub.events.updateDOM, [
        {
          liveRegion: this.config.liveRegionKey,
          html: this.extractLiveRegionHtml(html),
        },
      ]);
    }

    sleep(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    }
  },
);
